/**
 * パターン精度確認画面の表示用ユーティリティ。
 *
 * 精度集計（result の N+1 を伴う重い処理）は Functions 側の
 * `aggregatePatternAccuracy` Callable に寄せている。本ファイルでは
 * フロント側で表示時に使う「集計結果から % に変換する純粋計算」のみを扱う。
 */

/**
 * pattern_history_id 単位で集計した精度データ。
 * Callable レスポンスの stats 値とフィールドを一致させる必要がある。
 *
 * 精度は「帳票 1 枚あたりの平均」で出す（#3354）。
 * 帳票ごとに算出した精度（%）の合計と、その帳票数を受け取り、
 * 表示時に割って平均を出す。文字数を合算してから割る方式ではないため、
 * 帳票の長さによらず 1 枚 1 票で扱われる。
 */
export interface PatternHistoryAccuracyStats {
  executionCount: number; // ocr_executions の件数（処理枚数）
  accuracyPercentSum: number; // 確認済み帳票の精度（%）の合計
  accuracyResultCount: number; // 精度を算出できた帳票数
  hasEditableData: boolean; // 集計に使えるデータが存在するか
}

/**
 * pattern_history_id が存在しないレガシーデータ用キー。
 * Callable 側の同名定数と一致させる必要がある。
 */
export const NO_HISTORY_KEY = '__no_history__';

/**
 * 帳票 1 枚あたりの平均精度を 0-100 の % で返す（#3354）。
 * 集計対象が無い・バージョンに紐づかない場合は null を返す
 * （呼び出し側で「-」を出す）。
 */
export const calcAccuracyPercent = (
  stats: PatternHistoryAccuracyStats | undefined,
): number | null => {
  if (!stats) return null;
  if (!stats.hasEditableData) return null;
  if (stats.accuracyResultCount <= 0) return null;
  const average = stats.accuracyPercentSum / stats.accuracyResultCount;
  return Math.round(average * 10) / 10; // 小数点1桁
};

/**
 * 1件の結果に対する「読み取り精度」を文字数ベースで算出する（#3354）。
 *
 * OCR値と最終保存値（＝人が確認した正解）を項目単位・明細セル単位で突き合わせ、
 * 「正解の文字数のうち、OCR が変更されずに済んだ文字数」の割合を返す。
 * 取得ルールの調整がどれだけ効いているかを測るための指標のため、
 * 比較対象の OCR 値には「最新の全項目実行」を使う（呼び出し側で解決する）。
 *
 * #2960 の項目単位の完全一致から文字数判定に変更した（#3354）。
 * 商品名のような長い項目の 1 文字違いがセルまるごと 0 点になり、
 * 取得ルールを改善して正解に近づいた分が数字に出なかったため。
 *
 * 一致文字数は編集距離（レーベンシュタイン距離）で求める。
 * 分子 = 正解の文字数 − 編集距離 とすることで、1 文字違いなら
 * 残りの文字は一致として加点される。Functions 側のパターン精度集計
 * （aggregatePatternAccuracy の countChangedChars）と同じ定義に揃えている。
 *
 * 表（明細）では、値が別の列・別の行に入る「位置ズレ」は同一セル位置での
 * 値不一致として検出される（位置が合っているかを第一の評価軸とする）。
 */
/**
 * 精度計算に必要な項目定義の最小形。
 * 画面側は camelCase 変換後の item_list を渡す（fixedValue のみ camelCase、
 * is_ocr_target / is_calculated は変換後も snake_case のまま）。
 */
export interface ResultAccuracyItem {
  id: string;
  is_ocr_target?: boolean;
  fixedValue?: string;
  is_calculated?: boolean;
  children?: ResultAccuracyItem[];
}

/**
 * AI が読み取っていない項目かどうか。該当する項目は精度の分母に入れない。
 *
 * 精度は「AI がどれだけ正しく読めたか」を測るものなので、
 * AI が値を出していない項目を含めると実態とずれる。
 *
 * - is_ocr_target: false … 手入力項目。AI は値を出さないため、人が入力した
 *   文字がそのまま不一致として計上され、精度が不当に下がる
 * - fixedValue あり … 固定値。常に一致するので分母だけが膨らみ、精度が甘く出る
 * - is_calculated: true … 計算フィールド。同上
 */
const isNonOcrItem = (item: ResultAccuracyItem): boolean =>
  item.is_ocr_target === false || Boolean(item.fixedValue) || item.is_calculated === true;

export interface ResultAccuracyResult {
  matchedChars: number; // 変更されずに済んだ文字数（分子）
  totalChars: number; // 正解（最終保存値）の総文字数（分母）
  percent: number | null; // 0-100（小数1桁）。totalChars が 0 なら null
  // 明細の行数差（OCR行数 − 最終保存行数 の合計）。
  // 正 = OCRが余分な行を作った / 負 = 行を取りこぼした。0 なら行数は一致。
  // 母数を最終保存基準に固定するため、行数のズレはこの値で別途報告する。
  rowDiff: number;
}

/**
 * 精度計算専用の値正規化。表記ゆれ（カンマ・空白・全角半角・日付区切り）だけを
 * 吸収し、それ以外は素直な文字列比較にする。
 *
 * ※ 画面の変更検知用 normalizeValue は dayjs の緩いパースで数字列（例 8578675）を
 *   日付（8583/07/05）に誤変換してしまうため、精度計算には使えない。
 *   ここでは「区切り記号を含む明確な日付形式」だけを日付として正規化し、
 *   区切りなしの数字列は数値として扱う。
 */
const normalizeForAccuracy = (input: unknown): string => {
  // { value, ... } 形式の値セルは value を取り出す
  let val: unknown = input;
  if (val !== null && typeof val === 'object' && 'value' in (val as object)) {
    val = (val as Record<string, unknown>).value;
  }
  if (val === null || val === undefined) return '';

  // Firestore Timestamp 形式（{ seconds }）は日付文字列に
  if (typeof val === 'object' && 'seconds' in (val as object)) {
    const d = new Date((val as { seconds: number }).seconds * 1000);
    return `${d.getFullYear()}/${d.getMonth() + 1}/${d.getDate()}`;
  }

  if (typeof val === 'number') return String(val);

  let s = String(val).trim();
  if (s === '') return '';

  // 全角英数を半角に
  s = s.replace(/[Ａ-Ｚａ-ｚ０-９]/g, ch => String.fromCharCode(ch.charCodeAt(0) - 0xfee0));

  // 区切り記号（/ - . 年月日）を含む「日付らしい」文字列のみ日付として正規化する。
  // 例: 2025/05/08, 2025-5-8, 2025年5月8日 → 2025/5/8。区切りなしの数字列は対象外。
  const dateMatch = s.match(/^(\d{4})\s*[/\-.年]\s*(\d{1,2})\s*[/\-.月]\s*(\d{1,2})\s*[日]?$/);
  if (dateMatch) {
    const [, y, m, d] = dateMatch;
    return `${Number(y)}/${Number(m)}/${Number(d)}`;
  }

  // 数値（カンマ区切りや小数を含む）は数値として正規化（2,630.0 → 2630）
  const numStr = s.replace(/,/g, '');
  if (/^-?\d+(\.\d+)?$/.test(numStr)) {
    return String(Number(numStr));
  }

  // それ以外は空白を詰めた素の文字列で比較
  return s.replace(/\s+/g, '');
};

/**
 * 編集距離（レーベンシュタイン距離）で「変更された文字数」を数える。
 *
 * 置換を 1 文字として数える（削除1+挿入1の二重計上をしない）。
 * OCR の手修正は多くが誤認識文字の置き換えのため、二重計上すると
 * 精度が不当に低く出る。Functions 側の countChangedChars と同じ定義。
 *
 * 文字列が極端に長い場合は DP を避けて長さ差にフォールバックする
 * （長さ差は編集距離の下限なので安全側）。
 */
const COUNT_CHANGED_CHARS_MAX = 4000;

export const countChangedChars = (before: string, after: string): number => {
  if (before === after) return 0;
  // 過大なテキスト同士の DP を回避（精度より画面の応答性を優先）
  if (before.length + after.length > COUNT_CHANGED_CHARS_MAX) {
    return Math.abs(before.length - after.length);
  }
  const m = before.length;
  const n = after.length;
  // 1 次元 DP で Levenshtein 距離を算出（メモリ削減）
  let prev = new Array<number>(n + 1);
  let curr = new Array<number>(n + 1);
  for (let j = 0; j <= n; j++) prev[j] = j;
  for (let i = 1; i <= m; i++) {
    curr[0] = i;
    for (let j = 1; j <= n; j++) {
      if (before[i - 1] === after[j - 1]) {
        curr[j] = prev[j - 1];
      } else {
        // 置換 / 削除 / 挿入 のうち最小コスト + 1
        curr[j] = 1 + Math.min(prev[j - 1], prev[j], curr[j - 1]);
      }
    }
    [prev, curr] = [curr, prev];
  }
  return prev[n];
};

/**
 * 1セル分の文字数を集計に足し込む。
 *
 * 分母は正解（最終保存値）の文字数。分子は「変更されずに済んだ文字数」＝
 * 正解の文字数 − 編集距離。OCR が正解より長い値を返した場合は編集距離が
 * 正解の文字数を上回りうるため、分子は 0 で下げ止める。
 */
const accumulateCell = (
  ocrValue: unknown,
  correctValue: unknown,
): { matchedChars: number; totalChars: number } => {
  const ocrText = normalizeForAccuracy(ocrValue);
  const correctText = normalizeForAccuracy(correctValue);
  const totalChars = correctText.length;
  if (totalChars === 0) return { matchedChars: 0, totalChars: 0 };
  const changed = countChangedChars(ocrText, correctText);
  return { matchedChars: Math.max(0, totalChars - changed), totalChars };
};

export const calcResultAccuracy = (
  initialResultData: Record<string, unknown> | null | undefined,
  currentResultData: Record<string, unknown> | null | undefined,
  itemList: ResultAccuracyItem[],
): ResultAccuracyResult => {
  if (!initialResultData || !currentResultData) {
    return { matchedChars: 0, totalChars: 0, percent: null, rowDiff: 0 };
  }

  let matchedChars = 0;
  let totalChars = 0;
  let rowDiff = 0;

  for (const item of itemList) {
    // AI が読み取っていない項目（手入力・固定値・計算フィールド）は分母に入れない
    if (isNonOcrItem(item)) continue;

    const isArrayField = item.children && item.children.length > 0;
    if (isArrayField) {
      const initialRows =
        (initialResultData[item.id] as Array<Record<string, unknown>> | undefined) ?? [];
      const currentRows =
        (currentResultData[item.id] as Array<Record<string, unknown>> | undefined) ?? [];
      // 母数は「最終保存の行（=正解の行）」に固定する。
      // OCRの行数が違ってもエンジン間で母数が揃い、%を比較できる。
      // 行数のズレ（過剰行・行落ち）は rowDiff として別途報告する。
      const rowCount = currentRows.length;
      rowDiff += initialRows.length - currentRows.length;
      for (const child of item.children ?? []) {
        // 明細の列も同様に、AI が読み取っていない列は分母に入れない
        if (isNonOcrItem(child)) continue;
        for (let i = 0; i < rowCount; i++) {
          const cell = accumulateCell(initialRows[i]?.[child.id], currentRows[i]?.[child.id]);
          matchedChars += cell.matchedChars;
          totalChars += cell.totalChars;
        }
      }
    } else {
      const cell = accumulateCell(initialResultData[item.id], currentResultData[item.id]);
      matchedChars += cell.matchedChars;
      totalChars += cell.totalChars;
    }
  }

  const percent = totalChars > 0 ? Math.round((matchedChars / totalChars) * 1000) / 10 : null;
  return { matchedChars, totalChars, percent, rowDiff };
};
